import type {
    MouseEvent as ReactMouseEvent,
    ReactElement,
    ReactNode,
} from 'react';
import {
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import type { ManufactureDetail } from '@buying/library';

import { HighlightText } from '@bestseller-bit/frontend-community.components.highlight-text';
import {
    AngleDownIcon,
    AngleUpIcon,
} from '@bestseller-bit/frontend-community.theme.buying-theme';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import ListSubheader from '@mui/material/ListSubheader';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { debounce } from 'lodash';

import { useManufactureDetails } from '../../providers/ExtendedManufactureDetailsProvider';

import { MaterialOnTheFlyValueContext } from './reducer';

type ManufactureDetailWithManufacture = ManufactureDetail & {
    manufacture?: {
        manufactureName: string;
        manufacturePublicId: number;
        isDeleted: boolean;
    } | null;
};

// Manufacture detail is not available (deleted or no longer allowed)
const manufactureDetailNotAvailableMessage =
    'Manufacture detail is not available';
// if manufacture name is not available (deleted)
const manufactureNotAvailableMessage = 'Manufacture is not available';

/**
 * @description This function finds manufacture details with recurring names and returns an array of their public ids
 * @param arr - The array of manufacture details to find duplicate names in
 * @returns An array of the public ids of each detail with a recurring name
 */
const getManufactureDetailPublicIdsWithSameName = (
    arr: Array<ManufactureDetail>
): Array<string> => {
    const nameCounts: Record<string, Array<string>> = {};

    // Count the occurrences of each name
    arr.forEach((obj) => {
        const { manufactureDetailName, manufactureDetailPublicId } = obj;
        nameCounts[manufactureDetailName] =
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            (nameCounts[manufactureDetailName] || []).concat(
                manufactureDetailPublicId
            );
    });

    // Filter and flatten the indexes
    return Object.values(nameCounts)
        .filter((publicIds) => publicIds.length > 1)
        .flat();
};

// this prop is a tri-state(mandatory, optional o false) for Bom but always true for extended
interface ManufactureDetailsSectionProps {
    isRequired: boolean;
    isNonApparel?: boolean;
    onValidityChange?: (isValid: boolean) => void;
}

const ManufactureDetailsSection = ({
    isRequired,
    isNonApparel = false,
    onValidityChange: handleValidityChange,
}: ManufactureDetailsSectionProps): ReactElement | null => {
    // This observer is used to fetch more manufactureDetails when the last manufacture detail comes into view
    const observer = useRef<IntersectionObserver | null>(null);

    const [isTouched, setIsTouched] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
        new Set()
    );

    const { materialOnTheFlyValue, dispatchMaterialOnTheFly } = useContext(
        MaterialOnTheFlyValueContext
    );

    const {
        manufactureDetails,
        isLoading,
        fetchMore,
        generalQuery,
        setGeneralQuery,
    } = useManufactureDetails();

    // The freshly fetched (network-only) option matching the currently selected detail, if any.
    const matchingManufactureDetailOption = useMemo(() => {
        const selectedDetail = materialOnTheFlyValue.manufactureDetail;
        if (!selectedDetail) return undefined;
        return manufactureDetails.find(
            (detail) =>
                detail.manufactureDetailPublicId ===
                selectedDetail.manufactureDetailPublicId
        );
    }, [materialOnTheFlyValue.manufactureDetail, manufactureDetails]);

    // Validation: check if the currently selected manufacture detail is still available
    const manufactureDetailInvalidReason = useMemo(():
        | 'manufacture'
        | 'manufactureDetail'
        | undefined => {
        const selectedDetail = materialOnTheFlyValue.manufactureDetail;

        // If no detail is selected, it's valid (required-ness is handled separately)
        if (!selectedDetail) return undefined;

        // BOM's manufacture-details endpoint (bom-bff) exposes the parent manufacture relation, so
        // prefer its up-to-date status over the possibly stale value stored in form state. The
        // extended-composition-bff endpoint doesn't expose that relation at all, so fall back to
        // the selected value then.
        const manufacture =
            matchingManufactureDetailOption &&
            'manufacture' in matchingManufactureDetailOption
                ? (
                      matchingManufactureDetailOption as ManufactureDetailWithManufacture
                  ).manufacture
                : 'manufacture' in selectedDetail
                  ? selectedDetail.manufacture
                  : undefined;
        if (manufacture?.isDeleted) return 'manufacture';

        // Invalid if the detail itself has been deleted, or no longer exists among the available options.
        // Skip the "missing from options" check while still loading, otherwise the list being briefly
        // empty flashes a false invalid state.
        if (
            selectedDetail.isDeleted ||
            (!isLoading && !matchingManufactureDetailOption)
        )
            return 'manufactureDetail';

        return undefined;
    }, [
        materialOnTheFlyValue.manufactureDetail,
        matchingManufactureDetailOption,
        isLoading,
    ]);

    const isManufactureDetailValid = !manufactureDetailInvalidReason;

    const manufactureDetailErrorMessage = useMemo((): string | undefined => {
        if (manufactureDetailInvalidReason === 'manufacture') {
            return manufactureNotAvailableMessage;
        }
        if (manufactureDetailInvalidReason === 'manufactureDetail') {
            return manufactureDetailNotAvailableMessage;
        }
        if (
            isTouched &&
            isRequired &&
            !materialOnTheFlyValue.manufactureDetail
        ) {
            return 'Required field';
        }
        return undefined;
    }, [
        manufactureDetailInvalidReason,
        isTouched,
        isRequired,
        materialOnTheFlyValue.manufactureDetail,
    ]);

    // Hide options whose parent manufacture has been deleted, they can't be selected anymore
    const availableManufactureDetails = useMemo(
        () =>
            manufactureDetails.filter(
                (detail) =>
                    !(detail as ManufactureDetailWithManufacture).manufacture
                        ?.isDeleted
            ),
        [manufactureDetails]
    );

    // Sort by manufacture name so MUI groupBy renders groups contiguously
    const sortedManufactureDetails = useMemo(() => {
        if (!isNonApparel) return availableManufactureDetails;
        return availableManufactureDetails.toSorted((a, b) => {
            const nameA =
                (a as ManufactureDetailWithManufacture).manufacture
                    ?.manufactureName ?? '';
            const nameB =
                (b as ManufactureDetailWithManufacture).manufacture
                    ?.manufactureName ?? '';
            return nameA.localeCompare(nameB);
        });
    }, [isNonApparel, availableManufactureDetails]);

    // Groups start collapsed, so the last option's li never mounts. Attach the pagination
    // sentinel to the last group's header instead, which is always rendered.
    const lastGroupName = isNonApparel
        ? ((
              sortedManufactureDetails[sortedManufactureDetails.length - 1] as
                  | ManufactureDetailWithManufacture
                  | undefined
          )?.manufacture?.manufactureName ?? '')
        : undefined;

    const lastManufactureDetailRef = useCallback(
        (node: HTMLLIElement | null) => {
            // If the data is still loading, return early
            if (isLoading) return;

            // Disconnect the previous IntersectionObserver instance if it exists
            if (observer.current) observer.current.disconnect();

            // Create a new IntersectionObserver instance
            observer.current = new IntersectionObserver((entries) => {
                // When the last manufacture detail element is intersecting the viewport, fetch more data
                if (entries[0].isIntersecting) {
                    fetchMore();
                }
            });

            // Observe the last manufacture detail element
            if (node) observer.current.observe(node);
        },
        [isLoading, fetchMore]
    );

    // Debounce is used to avoid making a request for every key stroke (throttling)
    const debouncedSetGeneralQuery = debounce(setGeneralQuery, 300);
    const handleManufactureDetailInputChange = (newValue: string): void => {
        debouncedSetGeneralQuery(newValue);
    };

    const toggleGroup = (groupName: string): void => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupName)) {
                next.delete(groupName);
            } else {
                next.add(groupName);
            }
            return next;
        });
    };

    // Reset touched state when material subcategory changes (new selection or dialog reopen via clear_mof)
    useEffect(() => {
        setIsTouched(false);
    }, [materialOnTheFlyValue.materialSubcategory]);

    // If every fetched detail so far has a deleted parent manufacture, sortedManufactureDetails is
    // empty, so no option/group mounts to host the pagination sentinel. Keep fetching until a
    // selectable detail shows up (or fetchMore no-ops because there's no next page).
    useEffect(() => {
        if (isLoading) return;
        if (sortedManufactureDetails.length > 0) return;
        if (manufactureDetails.length === 0) return;
        fetchMore();
    }, [
        isLoading,
        sortedManufactureDetails.length,
        manufactureDetails.length,
        fetchMore,
    ]);

    // Notify parent about validity changes
    useEffect(() => {
        handleValidityChange?.(isManufactureDetailValid);
    }, [isManufactureDetailValid, handleValidityChange]);

    // Keep the stored manufacture detail's parent manufacture info in sync with the latest fetch, so
    // saving/validating the MOF always uses current data instead of a possibly stale/missing value.
    useEffect(() => {
        const selectedDetail = materialOnTheFlyValue.manufactureDetail;
        if (
            !selectedDetail ||
            !matchingManufactureDetailOption ||
            !('manufacture' in matchingManufactureDetailOption)
        )
            return;

        const freshManufacture =
            (
                matchingManufactureDetailOption as ManufactureDetailWithManufacture
            ).manufacture ?? null;
        const currentManufacture =
            'manufacture' in selectedDetail
                ? (selectedDetail.manufacture ?? null)
                : undefined;

        const isInSync =
            matchingManufactureDetailOption.manufactureDetailName ===
                selectedDetail.manufactureDetailName &&
            matchingManufactureDetailOption.isDeleted ===
                selectedDetail.isDeleted &&
            (freshManufacture?.manufacturePublicId ?? null) ===
                (currentManufacture?.manufacturePublicId ?? null) &&
            (freshManufacture?.manufactureName ?? null) ===
                (currentManufacture?.manufactureName ?? null) &&
            (freshManufacture?.isDeleted ?? null) ===
                (currentManufacture?.isDeleted ?? null);
        if (isInSync) return;

        dispatchMaterialOnTheFly({
            type: 'update_manufacture_detail',
            payload: {
                newManufactureDetail: {
                    ...selectedDetail,
                    ...matchingManufactureDetailOption,
                    manufacture: freshManufacture,
                },
            },
        });
    }, [
        materialOnTheFlyValue.manufactureDetail,
        matchingManufactureDetailOption,
        dispatchMaterialOnTheFly,
    ]);

    return (
        <Stack
            sx={{
                width: 1,
            }}
        >
            <FormLabel required={isRequired}>Manufacture details</FormLabel>
            <Autocomplete
                id="combo-box-demo"
                options={sortedManufactureDetails}
                sx={{
                    width: 1,
                    [`& .${autocompleteClasses.popupIndicator}`]: {
                        ['&:hover']: {
                            backgroundColor: 'transparent',
                        },
                    },
                }}
                slotProps={{
                    clearIndicator: { title: '' },
                    popupIndicator: { title: '' },
                }}
                loading={isLoading}
                value={materialOnTheFlyValue.manufactureDetail}
                // The selected value and the fetched options are different object instances
                isOptionEqualToValue={(option, selectedValue): boolean =>
                    option.manufactureDetailPublicId ===
                    selectedValue.manufactureDetailPublicId
                }
                disabled={!materialOnTheFlyValue.materialSubcategory}
                noOptionsText="No details found"
                groupBy={
                    isNonApparel
                        ? (option): string =>
                              (option as ManufactureDetailWithManufacture)
                                  .manufacture?.manufactureName ?? ''
                        : undefined
                }
                getOptionLabel={(option): string => {
                    if (isNonApparel) {
                        const manufactureName = (
                            option as ManufactureDetailWithManufacture
                        ).manufacture?.manufactureName;
                        if (!manufactureName)
                            return option.manufactureDetailName;
                        return `${manufactureName} \u2022 ${option.manufactureDetailName}`;
                    }

                    const duplicatePublicIds =
                        getManufactureDetailPublicIdsWithSameName(
                            sortedManufactureDetails
                        );
                    if (
                        duplicatePublicIds.includes(
                            option.manufactureDetailPublicId
                        )
                    )
                        return `${option.manufactureDetailName} (${option.manufactureDetailPublicId})`;
                    return option.manufactureDetailName;
                }}
                renderInput={(params): ReactNode => (
                    <TextField
                        {...params}
                        placeholder="Select"
                        value={generalQuery}
                        error={!isManufactureDetailValid}
                        fullWidth
                    />
                )}
                filterOptions={(x) => x}
                renderOption={(props, option, state) => {
                    const isLastOption =
                        !isNonApparel &&
                        sortedManufactureDetails[
                            sortedManufactureDetails.length - 1
                        ] === option;

                    return (
                        <li
                            ref={isLastOption ? lastManufactureDetailRef : null}
                            {...props}
                        >
                            <HighlightText
                                text={option.manufactureDetailName}
                                highlight={state.inputValue}
                            />
                        </li>
                    );
                }}
                {...(isNonApparel && {
                    renderGroup: (params): ReactNode => {
                        // Auto-expand groups while searching, otherwise matching options stay hidden
                        const isExpanded =
                            !!generalQuery || expandedGroups.has(params.group);
                        const isLastGroup = params.group === lastGroupName;
                        const handleMouseDown = (e: ReactMouseEvent): void => {
                            e.preventDefault();
                        };
                        return (
                            <Fragment key={params.key}>
                                <ListSubheader
                                    ref={
                                        isLastGroup
                                            ? lastManufactureDetailRef
                                            : null
                                    }
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={isExpanded}
                                    sx={{
                                        px: 3,
                                        py: 3,
                                        lineHeight: 'normal',
                                        cursor: 'pointer',
                                        ['&:hover']: {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                    onMouseDown={handleMouseDown}
                                    onKeyDown={(event): void => {
                                        if (
                                            event.key === 'Enter' ||
                                            event.key === ' '
                                        ) {
                                            event.preventDefault();
                                            toggleGroup(params.group);
                                        }
                                    }}
                                    onClick={(): void => {
                                        toggleGroup(params.group);
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                        }}
                                    >
                                        <Typography variant="buttons.small">
                                            {params.group || 'Other'}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: ({
                                                    palette,
                                                }: {
                                                    palette: {
                                                        text: {
                                                            primary: string;
                                                        };
                                                    };
                                                }) => palette.text.primary,
                                            }}
                                        >
                                            {isExpanded ? (
                                                <AngleUpIcon size={16} />
                                            ) : (
                                                <AngleDownIcon size={16} />
                                            )}
                                        </Box>
                                    </Box>
                                </ListSubheader>
                                {isExpanded && params.children}
                            </Fragment>
                        );
                    },
                })}
                onClose={(): void => {
                    setExpandedGroups(new Set());
                }}
                onBlur={(): void => {
                    setIsTouched(true);
                    setGeneralQuery('');
                }}
                onChange={(_event, newManufactureDetail): void => {
                    dispatchMaterialOnTheFly({
                        type: 'update_manufacture_detail',
                        payload: { newManufactureDetail },
                    });
                }}
                onInputChange={(_event, newInputValue, reason): void => {
                    if (reason === 'input') {
                        handleManufactureDetailInputChange(newInputValue);
                    } else {
                        // Selecting/clearing an option shouldn't leave a pending or stale search active
                        debouncedSetGeneralQuery.cancel();
                        setGeneralQuery('');
                    }
                }}
            />
            {manufactureDetailErrorMessage && (
                <FormHelperText error>
                    {manufactureDetailErrorMessage}
                </FormHelperText>
            )}
        </Stack>
    );
};

export {
    ManufactureDetailsSection,
    manufactureDetailNotAvailableMessage,
    manufactureNotAvailableMessage,
};
